import classNames from 'classnames'
import { motion, Variants } from 'framer-motion'
import { ReactNode } from 'react'

const DESKTOP_EASE_OUT = [0.16, 1, 0.3, 1] as const
const DESKTOP_EASE_IN = [0.7, 0, 0.84, 0] as const

export const variantsDesktop: Variants = {
  initial: {
    opacity: 0,
    y: 6,
    scale: 0.985,
    filter: 'blur(2px)'
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.32,
      ease: DESKTOP_EASE_OUT
    }
  },
  exit: {
    opacity: 0,
    y: -4,
    scale: 0.99,
    filter: 'blur(1px)',
    transition: {
      duration: 0.22,
      ease: DESKTOP_EASE_IN
    }
  }
}

const EASE_OUT = [0.16, 1, 0.3, 1] as const
const EASE_IN = [0.7, 0, 0.84, 0] as const

export const variantsMobile: Variants = {
  initial: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    y: 42,
    opacity: 0,
    transition: {
      y: { duration: 0.34, ease: EASE_OUT },
      opacity: { duration: 0.18, ease: EASE_OUT, delay: 0.06 } // fade starts just after movement begins
    }
  },

  animate: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    y: 0,
    opacity: 1,
    transition: {
      y: { duration: 0.34, ease: EASE_OUT },
      opacity: { duration: 0.22, ease: EASE_OUT, delay: 0.05 } // avoids “pop” right at start
    }
  },

  exit: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    y: 42,

    // key: hold opacity for most of the slide, then fade near the end
    opacity: [1, 1, 0],
    transition: {
      y: { duration: 0.3, ease: EASE_IN },
      opacity: { duration: 0.3, times: [0, 0.65, 1], ease: EASE_IN }
    }
  }
}

interface IProps {
  children: ReactNode
  isMobile: boolean
  className?: string
}

export const FormContainer = ({ children, isMobile, className }: IProps) => {
  if (isMobile === false) {
    return (
      <motion.div
        key='form-container-desktop'
        initial='initial'
        animate='animate'
        exit='exit'
        variants={variantsDesktop}
        className={classNames(
          'relative overflow-hidden bg-surface border border-border rounded-[35px] sm:h-auto pt-[60px] py-[2rem] px-[2rem] shadow-[0_18px_45px_rgba(15,23,42,0.10),0_4px_12px_rgba(15,23,42,0.06)]',
          className
        )}
      >
        {children}
      </motion.div>
    )
  }

  return (
    <motion.div
      key='form-container-mobile'
      initial='initial'
      animate='animate'
      exit='exit'
      variants={variantsMobile}
      className={classNames(
        'relative overflow-hidden block mx-auto bg-surface border border-border sm:h-auto rounded-t-[35px] pt-[60px] pb-[3.5rem] px-[1.5rem] shadow-[0_18px_45px_rgba(15,23,42,0.10),0_4px_12px_rgba(15,23,42,0.06)]',
        className
      )}
    >
      {children}
    </motion.div>
  )
}
